"use client"

/* eslint-disable functional/immutable-data -- The overlay's data attributes are the only state this player owns. */
import { useEffect } from "react"

import { createOpeningScene, type OpeningScene } from "./opening-scene"

/** ゲートが表示を決めたオープニングを canvas で再生する。描画する要素は持たない。 */
export function BrickOpeningPlayer({ id }: { id: string }) {
  useEffect(() => {
    const overlay = document.getElementById(id)
    const canvas = overlay?.querySelector("canvas")
    const state = overlay?.dataset.state
    if (!overlay || !canvas || (state !== "play" && state !== "run")) return
    let scene: OpeningScene
    try {
      // 読み込みが遅く CSS の安全装置（6 秒で消える）と重なるときは再生しない
      if (performance.now() > 4500) throw new Error("too late to play")
      scene = createOpeningScene(canvas)
    } catch {
      overlay.dataset.state = "done"
      overlay.dataset.intro = "reveal"
      return
    }
    let frame = 0
    let rippleSent = false
    // 抜けが始まる瞬間に、ヒーローを出し、ヒーロー側の土台へ波紋を引き継ぐ
    const reveal = () => {
      if (rippleSent) return
      rippleSent = true
      overlay.dataset.intro = "reveal"
      window.dispatchEvent(
        new CustomEvent("yo:stud-ripple", { detail: scene.impactPoint() }),
      )
    }
    const tick = (now: number) => {
      const phase = scene.frame(now)
      overlay.dataset.state = "run"
      if (phase !== "build") reveal()
      if (phase === "done") {
        overlay.dataset.state = "done"
        removeListeners()
        return
      }
      frame = requestAnimationFrame(tick)
    }
    const skip = () => {
      scene.skip()
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Tab" || event.key === "Shift") return
      skip()
    }
    const onHidden = () => {
      if (!document.hidden) return
      cancelAnimationFrame(frame)
      overlay.dataset.intro = "reveal"
      overlay.dataset.state = "done"
      removeListeners()
    }
    const onResize = () => scene.resize()
    function removeListeners() {
      overlay?.removeEventListener("pointerdown", skip)
      window.removeEventListener("keydown", onKey)
      window.removeEventListener("wheel", skip)
      window.removeEventListener("touchmove", skip)
      window.removeEventListener("resize", onResize)
      document.removeEventListener("visibilitychange", onHidden)
    }
    overlay.addEventListener("pointerdown", skip)
    window.addEventListener("keydown", onKey)
    window.addEventListener("wheel", skip, { passive: true })
    window.addEventListener("touchmove", skip, { passive: true })
    window.addEventListener("resize", onResize)
    document.addEventListener("visibilitychange", onHidden)
    frame = requestAnimationFrame(tick)
    // 開発時の再マウントでもやり直せるよう、状態は書き換えずに止めるだけ
    return () => {
      cancelAnimationFrame(frame)
      removeListeners()
    }
  }, [id])

  return null
}
